<template>
  <main class="grow">
    <section class="solutions-hero">
      <div class="mx-auto max-w-6xl px-4 sm:px-6">
        <div class="solutions-hero-inner">
          <div class="solutions-hero-copy">
            <h1>Find the Workspace369 setup for your kind of work.</h1>
            <span>
              Focused guides for service businesses comparing CRM, invoicing,
              project management, client portals, billing, and client-work
              platforms — what each one should do, and where Workspace369 fits.
            </span>
            <div class="hero-actions">
              <a
                :href="addReferralToUrl('https://app.workspace369.com/signup')"
                data-conversion-event="start_free_click"
                data-conversion-label="Solutions hub start free"
                data-conversion-location="solutions-hub-hero"
              >
                Start free trial
              </a>
              <NuxtLink :to="localePath('/compare')">Compare options</NuxtLink>
            </div>
            <TrialDisclosure />
          </div>

          <nav class="solutions-index" aria-label="Solutions by kind of work">
            <strong>{{ solutionPages.length }} focused guides</strong>
            <p>
              Each guide explains the problem, the workflow, the modules involved,
              and where Workspace369 fits.
            </p>
            <ol>
              <li v-for="group in groups" :key="group.id">
                <a :href="`#sol-${group.id}`">
                  <span>{{ group.title }}</span>
                  <small>{{ group.pages.length }}<span class="sr-only"> guides</span></small>
                </a>
              </li>
            </ol>
          </nav>
        </div>
      </div>
    </section>

    <section class="solutions-list">
      <div class="mx-auto max-w-6xl px-4 py-14 sm:px-6 md:py-20">
        <p class="solutions-spotlight">
          Agencies weighing team costs can start with
          <NuxtLink :to="localePath('/solutions/agency-software-flat-pricing')">all-in-one agency software with flat pricing instead of per-seat</NuxtLink>
          fees.
        </p>

        <section
          v-for="group in groups"
          :id="`sol-${group.id}`"
          :key="group.id"
          class="sol-group"
        >
          <div class="sol-head">
            <h2 class="sol-title">{{ group.title }}</h2>
            <span class="sol-count">{{ group.pages.length }}<span class="sr-only"> guides</span></span>
          </div>
          <div class="cards-grid">
            <NuxtLink
              v-for="page in group.pages"
              :key="page.slug"
              :to="localePath(page.slug)"
              class="solution-card"
            >
              <h3>{{ page.badge }}</h3>
              <p>{{ page.description }}</p>
              <span class="sol-open" aria-hidden="true">→</span>
            </NuxtLink>
          </div>
        </section>
      </div>
    </section>

    <!-- One buyer answer from each guide's own FAQ: search and AI answers read this hub more than
         the guides, so the answers are repeated here (data/solution-hub-answers.ts). -->
    <section class="sol-answers" aria-labelledby="sol-answers-title">
      <div class="mx-auto max-w-6xl px-4 pb-14 sm:px-6 md:pb-20">
        <div class="sol-head">
          <h2 id="sol-answers-title" class="sol-title">The short answer for each question</h2>
        </div>
        <ul class="sol-answer-list">
          <li v-for="answer in hubAnswers" :key="answer.path">
            <h3>{{ answer.q }}</h3>
            <p>{{ answer.a }}</p>
            <NuxtLink :to="localePath(answer.path)">Read the guide <span aria-hidden="true">→</span></NuxtLink>
          </li>
        </ul>
      </div>
    </section>

    <Cta03 />
    <PhoneStickyCta label="Solutions hub" :location="$route.path" watch=".solutions-hero .hero-actions" />
  </main>
</template>

<script setup>
import { localizedUrl } from "~/data/locales";
import solutionPages from "#build/page-data/solution-index.mjs";
import hubAnswers from "#build/page-data/solution-hub-answers.mjs";

const { addReferralToUrl } = useReferral();
const { canonicalLocaleCode, localePath } = useWorkspaceLocale();
const siteUrl = "https://workspace369.com";
const solutionsCanonicalUrl = computed(() => localizedUrl(siteUrl, "/solutions", canonicalLocaleCode.value));


// Hub sections. Each page lands in the first rule it matches, so the order is the priority; a page
// no rule matches goes to "More guides". Titles are plain job or audience names, no new routes.
const groupRules = [
  { id: "start-here", title: "Start with the platform", re: /^(service-business-management-software|client-work-operating-system|client-management-software|workspace-crm|crm-for-google-workspace|agency-software-flat-pricing)$/ },
  { id: "wellness", title: "Health, beauty and pet care", re: /(salon|spa|yoga|gym|nutrition|massage|chiropract|med-spa|hair|barber|nail|lash|esthetic|tattoo|makeup|dog|pet|personal-trainer)/ },
  { id: "property-retail", title: "Real estate, retail and e-commerce", re: /(real-estate|stager|property|mortgage|short-term|e-commerce-brands|shopify-store|wholesale|print-on-demand|manufactur|etsy|subscription)/ },
  { id: "events", title: "Events, travel and entertainment", re: /(event|wedding|dj|caterer|florist|venue|rental|booth|bands|travel)/ },
  { id: "professional", title: "Professional services", re: /(tax|law-firm|hr-|recruiter|financial|insurance|bookkeeping|architect|engineering|surveyor|landscape-arch|drafting|nonprofit|security)/ },
  { id: "jobs", title: "By job to be done", re: /^(?!crm-for-).*(post-call|auto-attendant|invoic|proposal|time-tracking|expense|client-portal|billing|onboarding|intake|payment|funnel|missed-call|scheduling|texting|inbox|phone|sms|voice|call-summary|contract-and|profitab|project-management)/ },
  { id: "agencies", title: "Agencies and studios", re: /(agenc|branding|web-design|seo-|msp|crm-for-(web-development|no-code|ai-automation|software-consultan))/ },
  { id: "advisors", title: "Consultants, coaches and educators", re: /(consult|coach|tutor|course|mastermind|membership|training|education|info-product|growth-programs|fractional|driving-school)/ },
  { id: "creatives", title: "Freelancers and creatives", re: /(freelanc|solopreneur|virtual-assist|video-editors|graphic|illustrat|motion|copywrit|ux-ui|voice-over|music|podcast|photograph|video-production|interior-design|shopify-e-commerce)/ },
];
const groups = computed(() => {
  const buckets = groupRules.map((rule) => ({ ...rule, pages: [] }));
  const rest = { id: "more", title: "More guides", pages: [] };
  for (const page of solutionPages) {
    const key = page.slug.replace(/^\/(solutions|features)\//, "").replace(/\/$/, "");
    (buckets.find((bucket) => bucket.re.test(key)) || rest).pages.push(page);
  }
  const order = ["start-here", "jobs", "agencies", "creatives", "advisors", "professional", "events", "wellness", "property-retail", "more"];
  return [...buckets, rest]
    .filter((group) => group.pages.length)
    .sort((left, right) => order.indexOf(left.id) - order.indexOf(right.id));
});


useHead({
  title: "Solutions for Service Businesses | Workspace369",
  meta: [
    {
      name: "description",
      content:
        "Explore Workspace369 solution pages for service business management, client work, invoicing, project management, client portals, billing, and CRM.",
    },
    { name: "twitter:title", content: "Solutions for Service Businesses | Workspace369" },
    {
      name: "twitter:description",
      content:
        "Workspace369 solution pages for service business software, client work, CRM, invoicing, portals, billing, and project management.",
    },
    { name: "twitter:card", content: "summary_large_image" },
    { property: "og:title", content: "Solutions for Service Businesses | Workspace369" },
    {
      property: "og:description",
      content:
        "Workspace369 solution pages for service business software, client work, CRM, invoicing, portals, billing, and project management.",
    },
  ],
  script: [
    {
      key: "workspace369-solutions-itemlist",
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "CollectionPage",
            "@id": `${solutionsCanonicalUrl.value}#collection`,
            name: "Workspace369 Solutions",
            url: solutionsCanonicalUrl.value,
            description:
              "Workspace369 solution pages for service business software, client work, CRM, invoicing, portals, billing, and project management.",
            hasPart: solutionPages.map((page) => ({
              "@type": "WebPage",
              name: page.badge,
              url: localizedUrl(siteUrl, page.slug, canonicalLocaleCode.value),
              description: page.description,
            })),
          },
        ],
      }),
    },
  ],
});
</script>

<style scoped>
.solutions-spotlight {
  margin: 0 0 2rem;
  color: #475569;
  line-height: 1.6;
}

.solutions-spotlight a {
  color: #0e7490;
  font-weight: 600;
  text-decoration: underline;
}

.dark .solutions-spotlight { color: #cbd5e1; }
.dark .solutions-spotlight a { color: #67e8f9; }

.solutions-hero {
  overflow: hidden;
  background:
    radial-gradient(circle at 84% 0%, rgb(27 167 206 / 0.13), transparent 27rem),
    linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
  color: #0f172a;
}

.dark .solutions-hero {
  background:
    radial-gradient(circle at 84% 18%, rgb(27 167 206 / 0.18), transparent 27rem),
    linear-gradient(180deg, #020617 0%, #0f172a 100%);
  color: #fff;
}

.solutions-hero-inner {
  display: grid;
  gap: 2rem;
  align-items: center;
  padding: 8.5rem 0 3.5rem;
}

.solutions-hero-copy { max-width: 56rem; min-width: 0; }

.solutions-hero h1 {
  color: #020617;
  font-size: clamp(2rem, 5.6vw, 4.25rem);
  font-weight: 950;
  letter-spacing: 0;
  line-height: 1.06;
  overflow-wrap: break-word;
  text-wrap: balance;
}

.dark .solutions-hero h1 { color: #fff; }

.solutions-hero-copy > span {
  display: block;
  margin-top: 1.35rem;
  max-width: 46rem;
  color: #475569;
  font-size: 1.04rem;
  line-height: 1.75;
}

.dark .solutions-hero-copy > span { color: #cbd5e1; }

.hero-actions {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 2rem;
}

.hero-actions a {
  display: inline-flex;
  min-height: 3rem;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  padding: 0.78rem 1.1rem;
  font-size: 0.92rem;
  font-weight: 850;
}

.hero-actions a:first-child { background: #0e7490; color: #fff; }
.hero-actions a:first-child:hover { background: #155e75; }
.hero-actions a:last-child { border: 1px solid #cbd5e1; color: #0f172a; }
.dark .hero-actions a:last-child { border-color: rgb(255 255 255 / 0.18); color: #fff; }

/* Jump index: the hub's own table of contents, in the app's list-card style. */
.solutions-index {
  min-width: 0;
  border: 1px solid #dbe4ee;
  border-radius: 10px;
  background: #fff;
  padding: 1.25rem 1.25rem 0.5rem;
  box-shadow: 0 18px 48px -28px rgb(15 23 42 / 0.25);
}
.dark .solutions-index {
  border-color: rgb(148 163 184 / 0.2);
  background: #0b1220;
  box-shadow: none;
}
.solutions-index strong {
  display: block;
  color: #0f172a;
  font-size: 1.35rem;
  font-weight: 900;
  line-height: 1.1;
}
.dark .solutions-index strong { color: #fff; }
.solutions-index > p {
  margin-top: 0.6rem;
  color: #64748b;
  font-size: 0.9rem;
  line-height: 1.6;
}
.dark .solutions-index > p { color: #94a3b8; }
.solutions-index ol { margin-top: 0.9rem; }
.solutions-index li + li { border-top: 1px solid #e5eaf0; }
.dark .solutions-index li + li { border-top-color: rgb(148 163 184 / 0.16); }
.solutions-index a {
  display: flex;
  min-height: 44px;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.55rem 0;
  color: #0f172a;
  font-size: 0.95rem;
  font-weight: 750;
}
.dark .solutions-index a { color: #e2e8f0; }
.solutions-index a:hover span:first-child,
.solutions-index a:focus-visible span:first-child { color: #0e7490; text-decoration: underline; text-underline-offset: 3px; }
.dark .solutions-index a:hover span:first-child,
.dark .solutions-index a:focus-visible span:first-child { color: #67e8f9; }
.solutions-index small {
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  padding: 0.05rem 0.4rem;
  color: #475569;
  font-size: 0.75rem;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
}
.dark .solutions-index small { border-color: rgb(148 163 184 / 0.35); color: #cbd5e1; }

.dark .solutions-list { background: #020617; }

/* ─── Sections ───────────────────────────────────────────────────────── */
.sol-group { scroll-margin-top: 7rem; }

.sol-answer-list {
  margin: 0;
  padding: 0;
  max-width: 50rem;
  list-style: none;
}

.sol-answer-list li {
  border-bottom: 1px solid #e2e8f0;
  padding: 1.4rem 0;
}

.sol-answer-list h3 {
  margin: 0;
  color: #0f172a;
  font-size: 1.05rem;
  font-weight: 800;
  line-height: 1.4;
}

.sol-answer-list p {
  margin: 0.5rem 0 0.6rem;
  color: #475569;
  font-size: 0.92rem;
  line-height: 1.7;
}

.sol-answer-list a {
  color: #0e7490;
  font-size: 0.82rem;
  font-weight: 700;
  text-decoration: underline;
  text-underline-offset: 3px;
}

.sol-answer-list a:focus-visible {
  outline: 2px solid #1ba7ce;
  outline-offset: 3px;
}

.dark .sol-answer-list li { border-bottom-color: #243247; }
.dark .sol-answer-list h3 { color: #f8fafc; }
.dark .sol-answer-list p { color: #94a3b8; }
.dark .sol-answer-list a { color: #67e8f9; }
.sol-group + .sol-group { margin-top: 2.75rem; }
.sol-head {
  display: flex;
  min-height: 44px;
  align-items: center;
  gap: 0.85rem;
  border-bottom: 1px solid #e2e8f0;
  padding-bottom: 0.85rem;
}
.dark .sol-head { border-bottom-color: #243247; }
.sol-title {
  flex: 1;
  margin: 0;
  color: #0f172a;
  font-size: clamp(1.4rem, 3vw, 2rem);
  font-weight: 900;
  line-height: 1.15;
}
.dark .sol-title { color: #f8fafc; }
.sol-count {
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  padding: 0.05rem 0.45rem;
  color: #475569;
  font-size: 0.8rem;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
}
.dark .sol-count { border-color: rgb(148 163 184 / 0.35); color: #cbd5e1; }

.cards-grid {
  display: grid;
  gap: 0.75rem;
  margin-top: 1.25rem;
}

.solution-card {
  position: relative;
  display: block;
  min-width: 0;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #fff;
  padding: 1rem 2.4rem 1rem 1.1rem;
  transition: border-color 160ms ease, box-shadow 160ms ease;
}
.dark .solution-card { border-color: #1e293b; background: #0b1220; }
.solution-card:hover,
.solution-card:focus-visible {
  border-color: rgb(14 116 144 / 0.5);
  box-shadow: 0 12px 30px -20px rgb(15 23 42 / 0.3);
}
.dark .solution-card:hover,
.dark .solution-card:focus-visible { border-color: rgb(103 232 249 / 0.4); box-shadow: none; }
.solution-card:focus-visible { outline: 2px solid #0e7490; outline-offset: 2px; }

.solution-card h3 {
  color: #0f172a;
  font-size: 1.02rem;
  font-weight: 850;
  line-height: 1.25;
}
.dark .solution-card h3 { color: #f8fafc; }

.solution-card p {
  display: -webkit-box;
  margin-top: 0.45rem;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  color: #475569;
  font-size: 0.88rem;
  line-height: 1.55;
}
.dark .solution-card p { color: #94a3b8; }

.sol-open {
  position: absolute;
  top: 1rem;
  right: 1rem;
  color: #0e7490;
  font-weight: 800;
}
.dark .sol-open { color: #67e8f9; }

@media (min-width: 540px) {
  .hero-actions { flex-direction: row; }
  .hero-actions a { min-width: 10rem; }
}

@media (min-width: 640px) {
  .cards-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}

@media (min-width: 768px) {
  .solutions-hero-inner { padding: 9rem 0 4.5rem; }
}

@media (min-width: 1024px) {
  .solutions-hero-inner {
    grid-template-columns: minmax(0, 1fr) minmax(20rem, 0.55fr);
    gap: 4rem;
  }
  .cards-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
}
</style>
